/**
 * Header component with search, filters, and actions
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { API_BASE } from '../../utils/constants';
import { authHeaders } from '../../services/api';

const Header = () => {
  const { 
    state, 
    hasSelection, 
    selectionCount, 
    clearSelection,
    setFilter,
    openModal,
    toggleSidebar
  } = useAppContext();
  
  const { logout, can } = useAuth();

  // Derive the current section from the URL so the title and actions
  // always match what's displayed regardless of how the user navigated.
  const { pathname } = useLocation();
  const currentView = pathname.split('/')[1] || 'interviews';
  const { ui } = state;

  // Content Studio screens render their own self-contained top bars, so the
  // global header is suppressed there (avoids a doubled header).
  const isContentStudio = currentView === 'content-studio';

  const [searchTerm, setSearchTerm] = useState('');
  
  // Debounce search to avoid excessive filtering
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  React.useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      setFilter(currentView, 'search', debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, currentView, setFilter]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  // 🚀 SIMPLE SCRAPE ALL FUNCTION - AS REQUESTED 🚀
  const handleScrapeAll = async () => {
    // Confirmation dialog
    const confirmed = window.confirm(
      '🔄 SCRAPE ALL INTERVIEWS\n\n' +
      'This will go through every interview in the database and scrape transcripts for those that need it.\n\n' +
      'The process will:\n' +
      '• Check each interview for existing transcript\n' +
      '• Scrape missing transcripts from their URLs\n' +
      '• Wait 1 second between each scrape\n' +
      '• May take several minutes to complete\n\n' +
      'Are you sure you want to proceed?'
    );

    if (!confirmed) return;

    try {
      console.log('🚀 Starting scrape all operation...');
      
      // Show loading state
      if (window.showToast) {
        window.showToast('🔄 Starting systematic transcript scraping...', 'info', 3000);
      }

      const response = await fetch(`${API_BASE}/interviews/scrape-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          const summary = result.summary;
          const successMsg = `✅ SCRAPE ALL COMPLETE!\n\n` +
            `📊 Summary:\n` +
            `• Total interviews: ${summary.total_interviews}\n` +
            `• Successfully scraped: ${summary.scraped_successfully}\n` +
            `• Already had transcripts: ${summary.already_had_transcript}\n` +
            `• No transcript URL: ${summary.no_transcript_url}\n` +
            `• Errors: ${summary.scraping_errors}\n` +
            `• Total time: ${summary.total_time_seconds.toFixed(1)}s`;
          
          alert(successMsg);
          
          if (window.showToast) {
            window.showToast(`✅ Scraped ${summary.scraped_successfully} interviews successfully!`, 'success', 5000);
          }
        } else {
          throw new Error(result.error || 'Scrape all operation failed');
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Scrape all failed (${response.status}): ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Scrape all error:', error);
      
      const errorMsg = `❌ SCRAPE ALL FAILED\n\n${error.message}`;
      alert(errorMsg);
      
      if (window.showToast) {
        window.showToast(`❌ Scrape all failed: ${error.message}`, 'error', 6000);
      }
    }
  };

  // 🚀 BLACKBELT TEAM CSV EXPORT FUNCTION - MISSION CRITICAL 🚀
  const handleExportCSV = async () => {
    try {
      console.log('📊 BLACKBELT CSV EXPORT - Starting download...');
      
      // Get current filters from app context
      const filters = state.filters?.interviews || {};
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add current filters to export
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.search) params.append('search', filters.search);
      
      // Configuration options for comprehensive export
      params.append('include_transcript', 'true');
      params.append('include_ai_fields', 'true');
      
      const exportUrl = `${API_BASE}/interviews/export/csv?${params.toString()}`;
      console.log('🎯 Export URL:', exportUrl);
      
      // Execute download with proper error handling
      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv',
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
      });
      
      if (response.ok) {
        // Get filename from response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'interviews_export.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        // Download the file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Get export stats from headers
        const exportCount = response.headers.get('X-Export-Count') || 'unknown';
        const generationTime = response.headers.get('X-Generation-Time') || 'unknown';
        
        console.log(`✅ BLACKBELT CSV EXPORT SUCCESS: ${exportCount} records in ${generationTime}`);
        
        // Show success notification (if available)
        if (window.showToast) {
          window.showToast(`✅ CSV export downloaded successfully!\n📊 ${exportCount} interviews exported`, 'success');
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Export failed (${response.status}): ${errorText}`);
      }
    } catch (error) {
      console.error('❌ BLACKBELT CSV EXPORT FAILED:', error);
      
      // Show error notification
      if (window.showToast) {
        window.showToast(`❌ CSV export failed: ${error.message}`, 'error');
      } else {
        alert(`❌ CSV export failed: ${error.message}`);
      }
    }
  };

  const getViewTitle = () => {
    const titles = {
      interviews: 'Interviews',
      articles: 'Articles',
      processing: 'AI Processing',
      activity: 'Activity Logs',
      // Without these, /sources fell through to "Dashboard" — the page title and the search
      // placeholder both read "dashboard" while the view underneath said Sources.
      sources: 'Sources',
      associations: 'Associations',
    };
    return titles[currentView] || 'Dashboard';
  };

  const getViewActions = () => {
    switch (currentView) {
      case 'interviews':
        return (
          <div className="flex items-center gap-2">
            {can('editorial.edit') && (
            <button
              onClick={() => openModal('csvUpload')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload CSV
            </button>
            )}
            <button
              onClick={() => handleExportCSV()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
            {can('editorial.edit') && (
            <>
            {/* "Scrape All" hidden. It ran the HTML scraper across every interview, which cannot
                read Fireflies (transcript renders client-side behind a login) and is unreliable
                on Otter. Pull transcripts per-interview from the detail page instead.
                handleScrapeAll is left intact above — uncomment this block to restore it. */}
            {/* <button
              onClick={() => handleScrapeAll()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Scrape All
            </button> */}
            <button
              onClick={() => openModal('addInterview')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-kc-blue text-white hover:bg-kc-blue-dk transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Interview
            </button>
            </>
            )}
          </div>
        );
      
      case 'articles':
        return null;
      
      case 'processing':
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openModal('batchProcessing')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Batch Process
            </button>
            <button
              onClick={() => openModal('processingDashboard')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-kc-blue text-white hover:bg-kc-blue-dk transition-colors"
            >
              Dashboard
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (isContentStudio) {
    return null;
  }

  return (
    <header className="h-12 bg-white border-b border-gray-200 px-4 flex items-center justify-between">

      {/* Left side - Title and sidebar toggle */}
      <div className="flex items-center">
        {ui.sidebarCollapsed && (
          <button
            onClick={toggleSidebar}
            className="p-1 text-gray-400 hover:text-gray-600 mr-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        
        <div>
          <h1 className="text-lg font-bold text-gray-900">
            {getViewTitle()}
          </h1>
          {hasSelection && (
            <p className="text-xs text-gray-500">
              {selectionCount} selected
              <button
                onClick={clearSelection}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Center - Search */}
      <div className="flex-1 max-w-md mx-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={`Search ${getViewTitle().toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center space-x-2">
        {getViewActions()}
        <button
          onClick={handleLogout}
          className="text-gray-500 hover:text-red-600 transition-colors flex items-center space-x-2 px-2 py-1 rounded"
          title="Logout"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </header>
  );
};

export default Header;